/**
 * Affiliate links + ARC-product links for the FIRE Tracker.
 *
 * Edit this file to add/remove/categorize products. Every link in the app
 * is sourced from here. Two helpers:
 *   - withTracking(url, source) → appends UTM params for analytics
 *   - getLinksByCategory(category) → filter helper
 *
 * Sources of truth:
 *   - "Affiliate Links" sheet (Gven's master sheet, pulled May 2026)
 *   - ARC owned products (Galaxy $147, ApexOne $4K)
 */

/* ─── Brand color palette (used by AffiliateCard for branded tiles) ─── */
// Each color was picked from the brand's official site / brand kit. Used as
// the accent color on the card's logo tile so the card is recognizable
// without needing a real logo SVG.
export const BRAND_COLORS = {
  bitdefender: "#ED1C24", // BitDefender red
  cryptocom: "#103F68",   // Crypto.com navy
  etherfi: "#9893FE",     // EtherFi purple
  kraken: "#5741D9",      // Kraken purple
  ledger: "#000000",      // Ledger black
  nordvpn: "#4687FF",     // NordVPN blue
  gainstrade: "#9B7EFF",  // Gains.Trade purple
  hyperliquid: "#50D2C1", // Hyperliquid teal
  lighter: "#1B1B1B",     // Lighter black
  wisprflow: "#3B82F6",   // Wispr Flow blue
  galaxy: "#E0B450",      // ARC gold (Galaxy)
  apexone: "#FFD66B",     // ARC light gold (Apex)
};

/* ─── UTM tracking helper ──────────────────────────────────────────── */
// Appends UTM params to track which placement converts. Source is always
// "fire-tracker"; medium tells us WHERE in the app the click came from.
export const withTracking = (url, medium = "tools-tab") => {
  if (!url) return "#";
  // If the URL already has a query string, use & instead of ?
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}utm_source=fire-tracker&utm_medium=${encodeURIComponent(
    medium
  )}&utm_campaign=affiliate-2026`;
};

/* ─── External affiliate products ──────────────────────────────────── */
// Categories appear as section headers on the Tools & Resources tab.
// "advanced" category is gated behind a warning subsection.
export const AFFILIATE_LINKS = [
  // ── Exchanges (centralized) ──
  {
    id: "kraken",
    name: "Kraken",
    url: "https://go.arcrypto.io/kraken",
    category: "exchanges",
    brandColor: BRAND_COLORS.kraken,
    iconLetter: "K",
    tagline: "Kraken Exchange",
    description:
      "Centralized exchange for buying, selling, and DCAing crypto. Strong security record, good fee structure.",
    swamiNote: "What I use to buy BTC/ETH for my long-term stack.",
    contextTags: ["investments", "dca", "tools"],
  },
  {
    id: "cryptocom",
    name: "Crypto.com",
    url: "https://go.arcrypto.io/cryptodc",
    category: "exchanges",
    brandColor: BRAND_COLORS.cryptocom,
    iconLetter: "C",
    tagline: "Crypto.com Exchange",
    description:
      "Centralized exchange with debit card, staking rewards, and 250+ crypto pairs. Good for everyday spending and DCA.",
    swamiNote: "Solid for spending crypto with the Visa card and earning rewards.",
    contextTags: ["investments", "dca", "tools"],
  },

  // ── Hardware / Wallets ──
  {
    id: "ledger",
    name: "Ledger",
    url: "https://go.arcrypto.io/ledger",
    category: "wallets",
    brandColor: BRAND_COLORS.ledger,
    iconLetter: "L",
    tagline: "Ledger Hardware Wallet",
    description:
      "Cold storage hardware wallet — your keys in a physical device, offline. Non-negotiable for serious crypto holders.",
    swamiNote:
      "Not your keys, not your crypto. If you have more than $1K in crypto, you need a hardware wallet.",
    contextTags: ["wallets", "tools"],
    priority: "high",
  },

  // ── DeFi / Yield ──
  {
    id: "etherfi",
    name: "EtherFi",
    url: "https://go.arcrypto.io/etherfi",
    category: "defi",
    brandColor: BRAND_COLORS.etherfi,
    iconLetter: "E",
    tagline: "EtherFi Liquid Restaking",
    description:
      "Liquid restaking protocol for ETH — earn staking yield + restaking rewards while keeping your ETH liquid.",
    swamiNote: "One of my go-to plays for putting ETH to work without locking it up.",
    contextTags: ["networth", "investments", "tools"],
  },

  // ── Security / Privacy ──
  {
    id: "ledger-security", // placeholder — we use ledger for wallets, but we can group differently
    skip: true, // not rendered, kept for future
  },
  {
    id: "bitdefender",
    name: "BitDefender",
    url: "https://go.arcrypto.io/bitdefender",
    category: "security",
    brandColor: BRAND_COLORS.bitdefender,
    iconLetter: "B",
    tagline: "BitDefender Antivirus",
    description:
      "Antivirus + device security. Critical when your computer is the gateway to your crypto wallets and exchange accounts.",
    swamiNote:
      "If your laptop gets compromised, your crypto can disappear in minutes. Don't skip this.",
    contextTags: ["tools"],
  },
  {
    id: "nordvpn",
    name: "NordVPN",
    url: "https://go.arcrypto.io/nordvpn",
    category: "security",
    brandColor: BRAND_COLORS.nordvpn,
    iconLetter: "N",
    tagline: "NordVPN",
    description:
      "VPN service — masks your IP and encrypts traffic. Useful when accessing exchanges/wallets on public Wi-Fi.",
    swamiNote: "I never log into a wallet on hotel Wi-Fi without it.",
    contextTags: ["tools"],
  },

  // ── Advanced Trading (perps / leverage) ──
  // ⚠ Gated section on the Tools tab — visible only after a "show advanced" toggle.
  {
    id: "hyperliquid",
    name: "Hyperliquid",
    url: "https://go.arcrypto.io/hyperliquid",
    category: "advanced",
    brandColor: BRAND_COLORS.hyperliquid,
    iconLetter: "H",
    tagline: "Hyperliquid DEX Perps",
    description:
      "Decentralized perpetuals exchange — trade with leverage on-chain, no KYC. ⚠ High risk product.",
    swamiNote: "Where I run my advanced perps strategies. Not for beginners.",
    contextTags: ["tools-advanced"],
  },
  {
    id: "lighter",
    name: "Lighter",
    url: "https://go.arcrypto.io/lighter",
    category: "advanced",
    brandColor: BRAND_COLORS.lighter,
    iconLetter: "L",
    tagline: "Lighter DEX Perps",
    description:
      "DEX perps platform with deep liquidity and tight spreads. ⚠ Leveraged trading — only for experienced traders.",
    swamiNote: "Tight spreads and good UX for advanced traders.",
    contextTags: ["tools-advanced"],
  },
  {
    id: "gainstrade",
    name: "Gains.Trade",
    url: "https://go.arcrypto.io/gains",
    category: "advanced",
    brandColor: BRAND_COLORS.gainstrade,
    iconLetter: "G",
    tagline: "Gains.Trade Leverage",
    description:
      "On-chain leveraged trading on Polygon/Arbitrum — crypto, forex, stocks. ⚠ Leveraged products only for experienced traders.",
    swamiNote: "Useful for hedging and leverage plays beyond just crypto pairs.",
    contextTags: ["tools-advanced"],
  },

  // ── Productivity / Bonus ──
  {
    id: "wisprflow",
    name: "Wispr Flow",
    url: "https://wisprflow.ai/r?GVEN1",
    category: "bonus",
    brandColor: BRAND_COLORS.wisprflow,
    iconLetter: "W",
    tagline: "Wispr Flow",
    description:
      "Voice-to-text dictation tool — write 4× faster by speaking. Bonus pick from Swami's productivity stack.",
    swamiNote: "How I draft most of my YouTube scripts and emails these days.",
    contextTags: ["tools-bonus"],
  },
];

/* ─── ARC's own products (Galaxy + ApexOne only per Gven's call) ───── */
// These get *premium* placement — Apex on Risk Assessment results, Galaxy
// on Goals tab, both featured at the top of the Tools tab.
export const ARC_PRODUCTS = {
  apexone: {
    id: "apexone",
    name: "ApexOne Strategy Call",
    url: "https://go.arcrypto.io/apexone",
    category: "arc-premium",
    brandColor: BRAND_COLORS.apexone,
    iconLetter: "A",
    tagline: "Talk 1-on-1 with an ARC Strategist",
    description:
      "Personalized portfolio strategy call. Get a tailored plan for your goals, risk profile, and timeline.",
    cta: "Book your strategy call",
    priceTag: "$4,000 program",
    badge: "Premium",
    contextTags: ["risk-results", "retirement", "networth", "tools"],
  },
  galaxy: {
    id: "galaxy",
    name: "ARC Galaxy",
    url: "https://go.arcrypto.io/joingalaxy",
    category: "arc-galaxy",
    brandColor: BRAND_COLORS.galaxy,
    iconLetter: "G",
    tagline: "Join ARC Galaxy",
    description:
      "ARC's flagship community + education program. Live sessions, vault breakdowns, and the Galaxy library.",
    cta: "Join for $147",
    priceTag: "$147",
    badge: "Most Popular",
    contextTags: ["overview", "goals", "tools"],
  },
};

/* ─── Helpers ──────────────────────────────────────────────────────── */
// Returns affiliate links for a specific UI surface, e.g. getLinksFor("wallets")
// returns Ledger because Ledger has "wallets" in its contextTags.
export const getLinksFor = (contextTag) =>
  AFFILIATE_LINKS.filter(
    (l) => !l.skip && Array.isArray(l.contextTags) && l.contextTags.includes(contextTag)
  );

// Returns links by primary category (excluding skipped)
export const getLinksByCategory = (category) =>
  AFFILIATE_LINKS.filter((l) => !l.skip && l.category === category);

// All categories in display order
export const TOOLS_CATEGORIES = [
  { key: "exchanges", label: "Exchanges", description: "For buying, selling, and DCAing crypto" },
  { key: "wallets",   label: "Wallets & Hardware", description: "Cold storage and self-custody" },
  { key: "defi",      label: "DeFi & Yield",       description: "Earn yield on your holdings" },
  { key: "security",  label: "Security & Privacy", description: "Protect your devices and connection" },
];

/* ─── FTC Disclosure (required for affiliate compliance) ───────────── */
export const FTC_DISCLOSURE = `Some links on this page are affiliate links. ARCrypto may earn a commission if you sign up — at no extra cost to you. Swami only recommends tools he uses or has personally vetted.`;
